/**
 * Contact Detail Card
 *
 * Eine Person im Chat: Profil, KI-Bewertung mit Begründung, Stand im Ablauf der
 * Kampagne und der Nachrichtenverlauf. Daten kommen aus campaign_contact_get bzw.
 * contact_get (Kontaktbuch, dann mit den Kampagnen der Person statt Verlauf).
 */

import React, { useState } from 'react';
import { Box, Paper, Typography, Chip, Stack, IconButton, Tooltip, Button } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';

export interface ContactDetailMessage {
  direction: 'sent' | 'received' | string;
  type?: string | null;
  content: string;
  at?: string | null;
}

export interface ContactDetailCardData {
  contact: {
    name?: string;
    title?: string | null;
    company?: string | null;
    location?: string | null;
    linkedinUrl?: string | null;
  };
  campaignName?: string | null;
  status?: {
    stage?: string | null;
    active?: boolean;
    finished?: boolean;
    endedReason?: string | null;
    waitUntil?: string | null;
    unread?: boolean;
  };
  rating?: {
    grade?: string | null;
    score?: number | null;
    explanation?: string | null;
  };
  messages?: ContactDetailMessage[];
  /** Wie viele Nachrichten es insgesamt gibt (die Karte zeigt nur die letzten). */
  messageCount?: number;
  /** contact_get: die Kampagnen, in denen die Person ist. */
  campaigns?: Array<{ campaignName?: string | null; stage?: string | null; grade?: string | null; finished?: boolean }>;
}

const STAGE_LABEL: Record<string, string> = {
  new: 'Neu',
  contacted: 'Kontaktiert',
  replied: 'Hat geantwortet',
  meeting_booked: 'Termin vereinbart',
  qualified: 'Qualifiziert',
  won: 'Gewonnen',
  lost: 'Verloren',
};

const GRADE_COLOR: Record<string, 'success' | 'warning' | 'default'> = { A: 'success', B: 'warning', C: 'default' };

function fmt(date?: string | null): string {
  if (!date) return '';
  const d = new Date(date);
  return Number.isNaN(d.getTime())
    ? String(date)
    : d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const EXPLANATION_PREVIEW = 280;
const MESSAGE_PREVIEW = 400;

const ContactDetailCard: React.FC<{ data: ContactDetailCardData }> = ({ data }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const [explanationOpen, setExplanationOpen] = useState(false);
  const [openMessages, setOpenMessages] = useState<Record<number, boolean>>({});

  const contact = data.contact || {};
  const status = data.status;
  const rating = data.rating;
  const messages = data.messages || [];
  const role = [contact.title, contact.company].filter(Boolean).join(' · ');
  const explanation = rating?.explanation || '';
  const longExplanation = explanation.length > EXPLANATION_PREVIEW;

  const stageLabel = status?.stage ? t(`contactStage_${status.stage}`, STAGE_LABEL[status.stage] ?? status.stage) : '';
  const flowLabel = status
    ? status.finished
      ? t('contactFlowFinished', 'Ablauf beendet')
      : status.active
        ? t('contactFlowActive', 'im Ablauf')
        : t('contactFlowInactive', 'nicht aktiv')
    : '';

  return (
    <Paper className={classes.card} elevation={0}>
      <Stack direction="row" alignItems="flex-start" gap={1}>
        <PersonIcon color="primary" fontSize="small" sx={{ mt: 0.25 }} />
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography variant="subtitle2" className={classes.title}>
            {contact.name || t('unknownContact', 'Unbekannter Kontakt')}
          </Typography>
          {role && (
            <Typography variant="caption" color="textSecondary" component="div">
              {role}
            </Typography>
          )}
          {(contact.location || data.campaignName) && (
            <Typography variant="caption" color="textSecondary" component="div">
              {[contact.location, data.campaignName && t('contactInCampaign', 'Kampagne: {{name}}', { name: data.campaignName })]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          )}
        </Box>
        {contact.linkedinUrl && (
          <Tooltip title={t('viewOnLinkedIn', 'Auf LinkedIn ansehen')}>
            <IconButton size="small" onClick={() => window.open(contact.linkedinUrl as string, '_blank', 'noopener')}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {(status || rating?.grade) && (
        <Stack direction="row" gap={0.75} flexWrap="wrap">
          {rating?.grade && (
            <Chip
              size="small"
              color={GRADE_COLOR[rating.grade] ?? 'default'}
              label={
                typeof rating.score === 'number'
                  ? t('contactGradeScore', 'Bewertung {{grade}} · {{score}}', { grade: rating.grade, score: rating.score })
                  : t('contactGrade', 'Bewertung {{grade}}', { grade: rating.grade })
              }
            />
          )}
          {stageLabel && <Chip size="small" variant="outlined" label={stageLabel} />}
          {flowLabel && <Chip size="small" variant="outlined" label={flowLabel} />}
          {status?.unread && <Chip size="small" color="primary" label={t('contactUnread', 'ungelesen')} />}
        </Stack>
      )}

      {status?.endedReason && (
        <Typography variant="caption" color="textSecondary">
          {t('contactEndedReason', 'Beendet: {{reason}}', { reason: status.endedReason })}
        </Typography>
      )}
      {!status?.finished && status?.waitUntil && (
        <Typography variant="caption" color="textSecondary">
          {t('contactWaitUntil', 'Nächster Schritt frühestens {{date}}', { date: fmt(status.waitUntil) })}
        </Typography>
      )}

      {explanation && (
        <Box className={classes.section}>
          <Typography variant="caption" className={classes.sectionTitle}>
            {t('contactRatingWhy', 'Warum diese Bewertung')}
          </Typography>
          <Typography variant="body2" className={classes.prewrap}>
            {explanationOpen || !longExplanation ? explanation : `${explanation.slice(0, EXPLANATION_PREVIEW).trimEnd()} …`}
          </Typography>
          {longExplanation && (
            <Button size="small" onClick={() => setExplanationOpen((v) => !v)} className={classes.more}>
              {explanationOpen ? t('showLess', 'weniger') : t('showMore', 'mehr')}
            </Button>
          )}
        </Box>
      )}

      {messages.length > 0 && (
        <Box className={classes.section}>
          <Typography variant="caption" className={classes.sectionTitle}>
            {data.messageCount && data.messageCount > messages.length
              ? t('contactMessagesLast', 'Nachrichten (letzte {{shown}} von {{count}})', { shown: messages.length, count: data.messageCount })
              : t('contactMessages', 'Nachrichten')}
          </Typography>
          {messages.map((m, i) => {
            const received = m.direction === 'received';
            const long = m.content.length > MESSAGE_PREVIEW;
            const open = openMessages[i];
            return (
              <Box key={i} className={cx(classes.message, received && classes.received)}>
                <Typography variant="caption" color="textSecondary" component="div">
                  {received ? contact.name || t('contactThem', 'Kontakt') : t('contactYou', 'Du')}
                  {m.type === 'connection_request' ? ` · ${t('contactConnectionNote', 'Kontaktanfrage')}` : ''}
                  {m.at ? ` · ${fmt(m.at)}` : ''}
                </Typography>
                <Typography variant="body2" className={classes.prewrap}>
                  {open || !long ? m.content || '—' : `${m.content.slice(0, MESSAGE_PREVIEW).trimEnd()} …`}
                </Typography>
                {long && (
                  <Button size="small" onClick={() => setOpenMessages((s) => ({ ...s, [i]: !open }))} className={classes.more}>
                    {open ? t('showLess', 'weniger') : t('showMore', 'mehr')}
                  </Button>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {data.campaigns && data.campaigns.length > 0 && (
        <Box className={classes.section}>
          <Typography variant="caption" className={classes.sectionTitle}>
            {t('contactCampaigns', 'In Kampagnen')}
          </Typography>
          {data.campaigns.map((c, i) => (
            <Stack key={i} direction="row" gap={1} alignItems="center">
              <Typography variant="body2" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
                {c.campaignName || '—'}
              </Typography>
              {c.grade && <Chip size="small" color={GRADE_COLOR[c.grade] ?? 'default'} label={c.grade} />}
              {c.stage && <Chip size="small" variant="outlined" label={t(`contactStage_${c.stage}`, STAGE_LABEL[c.stage] ?? c.stage)} />}
            </Stack>
          ))}
        </Box>
      )}
    </Paper>
  );
};

const useStyles = makeStyles()((theme) => ({
  card: {
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    maxWidth: 560,
  },
  title: {
    fontWeight: 600,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    paddingTop: theme.spacing(0.75),
    borderTop: `1px dashed ${theme.palette.divider}`,
  },
  sectionTitle: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  prewrap: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  message: {
    padding: theme.spacing(0.75, 1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
    alignSelf: 'flex-start',
    maxWidth: '92%',
  },
  received: {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.light + '33',
  },
  more: {
    alignSelf: 'flex-start',
    padding: 0,
    minWidth: 0,
    textTransform: 'none',
  },
}));

export default ContactDetailCard;
